import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Star, Users, Home } from "lucide-react";

const PriceOverview = () => {
  // Retainer-Pakete für Kleinunternehmen
  const businessRetainers = [
    {
      name: "Retainer Basis",
      price: "ab 99 €",
      unit: "/ Monat",
      description: "Für Solo-Selbstständige und kleine Büros",
      features: [
        "1× monatlicher IT-Gesundheitscheck",
        "30 Min. Remote-Support inkl.",
        "Reaktionszeit: 48h (Mo-Fr)",
        "10% Rabatt auf zusätzliche Leistungen"
      ],
      recommended: false
    },
    {
      name: "Retainer Standard",
      price: "ab 149 €",
      unit: "/ Monat",
      description: "Für kleine Teams mit dauerhaft funktionierender IT",
      features: [
        "1× monatlicher IT-Gesundheitscheck",
        "60 Min. Remote-Support inkl.",
        "Reaktionszeit: 24h (Mo-Fr)",
        "15% Rabatt auf zusätzliche Leistungen"
      ],
      recommended: true
    },
    {
      name: "Retainer Premium",
      price: "ab 199 €",
      unit: "/ Monat",
      description: "Für Betriebe, bei denen IT-Ausfälle teuer werden",
      features: [
        "1× monatlicher IT-Gesundheitscheck",
        "120 Min. Remote-Support inkl.",
        "1 Vor-Ort-Einsatz/Quartal (bis 2 Std.)",
        "Priorisierte Bearbeitung (am selben Werktag)",
        "20% Rabatt auf zusätzliche Leistungen"
      ],
      recommended: false
    }
  ];

  // Einmal-Support für Kleinunternehmen
  const businessOneTime = [
    { service: "Remote-Support", price: "69,00 €", unit: "/ Std." },
    { service: "Vor-Ort-Support", price: "90,00 €", unit: "/ Std." },
    { service: "IT-Check für Kleinunternehmen", price: "103,50 €", unit: "(einmalig)" },
    { service: "Website-Wartung WordPress", price: "ab 48,00 €", unit: "/ Monat" }
  ];

  // Sorglos-Pakete für Senioren
  const seniorPackages = [
    {
      name: "Sorglos Zuhause Basic",
      price: "19 €",
      unit: "/ Monat",
      features: [
        "1× Remote-Sicherheitscheck alle 6 Monate",
        "15 Min. Telefonsupport/Monat inkl.",
        "10% Rabatt auf Vor-Ort-Termine"
      ]
    },
    {
      name: "Sorglos Zuhause Plus",
      price: "39 €",
      unit: "/ Monat",
      features: [
        "1× Remote-Sicherheitscheck pro Quartal",
        "30 Min. Telefonsupport/Monat inkl.",
        "Bevorzugte Terminvergabe",
        "15% Rabatt auf Vor-Ort-Termine"
      ]
    }
  ];

  // Einzelpreise für Senioren
  const seniorPrices = [
    { service: "Remote-Support Kurz-Hilfe", price: "30,00 €", unit: "(30 Min.)" },
    { service: "Remote-Support Standard-Sitzung", price: "50,00 €", unit: "(60 Min.)" },
    { service: "Remote-Support Verlängerung", price: "12,50 €", unit: "(je 15 Min.)" },
    { service: "Vor-Ort-Support erste Stunde", price: "70,00 €", unit: "(60 Min.)" },
    { service: "Vor-Ort-Support Verlängerung", price: "15,00 €", unit: "(je 15 Min.)" },
    { service: "Schulungen (Remote)", price: "50,00 €", unit: "(60 Min.)" },
    { service: "Schulungen (Vor-Ort)", price: "70,00 €", unit: "(60 Min.)" },
    { service: "Remote-Support Paket S50 (Prepaid)", price: "140,00 €", unit: "(5×30 Min.)" }
  ];

  return (
    <div className="space-y-8">
      {/* Kleinunternehmen & Freiberufler */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Kleinunternehmen & Freiberufler – IT-Betreuung im Monatsabo</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Statt einzelne Einsätze zu bezahlen, sichern sich Kunden mit unseren Retainer-Paketen eine laufende IT-Betreuung.
        </p>

        {/* Retainer-Pakete */}
        <div className="grid gap-4 md:grid-cols-3">
          {businessRetainers.map((pkg, index) => (
            <Card key={index} className={pkg.recommended ? "border-primary border-2 relative" : "border"}>
              {pkg.recommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                  <Star className="h-3 w-3 mr-1" /> Empfohlen
                </Badge>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary mb-4">
                  {pkg.price} <span className="text-sm font-normal text-muted-foreground">{pkg.unit}</span>
                </div>
                <ul className="space-y-2 text-sm">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-600">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Einmal-Support */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Einmal-Support ohne Vertrag</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Leistung</TableHead>
                  <TableHead className="text-right">Preis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businessOneTime.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{item.service}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {item.price} {item.unit}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Senioren & Privatpersonen */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Home className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Senioren & Privatpersonen – Sorglos-Pakete</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Für alle, die regelmäßig Unterstützung brauchen, aber nicht jedes Mal neu überlegen wollen, wen sie anrufen.
        </p>

        {/* Sorglos-Pakete */}
        <div className="grid gap-4 md:grid-cols-2">
          {seniorPackages.map((pkg, index) => (
            <Card key={index} className="border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary mb-4">
                  {pkg.price} <span className="text-sm font-normal text-muted-foreground">{pkg.unit}</span>
                </div>
                <ul className="space-y-2 text-sm">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-600">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Einzelpreise */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Flexible Hilfe (Einzelpreise)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Leistung</TableHead>
                  <TableHead className="text-right">Preis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {seniorPrices.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{item.service}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {item.price} {item.unit}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PriceOverview;